import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import withRouter from "../../components/Common/withRouter";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// QR Scanner
import { Html5Qrcode } from "html5-qrcode";

// reusable components
import CustomInput from "../../components/AuthenticationComponents/CustomInput";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import logo from "../../assets/images/authentication/logo-auth.svg";
import "../../assets/scss/custom/pages/Authentication.scss";
import creditCardIcon from "../../assets/images/authentication/credit-card.svg";
import giftIcon from "../../assets/images/authentication/gift.svg";
import zapIcon from "../../assets/images/authentication/zap.svg";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";

const RestaurantRedeemVoucher = (props) => {
  document.title = "Redeem Voucher | Omaha Food Lovers";

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const scannerRef = useRef(null);
  const errorToastShownRef = useRef(false);
  const isScanningRef = useRef(false);

  useEffect(() => {
    document.body.className = "authentication-bg";
    window.scrollTo(0, 0);

    // Check if device has camera access (for QR scanning)
    const checkCameraAccess = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some(
          (device) => device.kind === "videoinput",
        );
        setIsMobileOrTablet(hasCamera);
      } catch (error) {
        // Fallback to user agent detection if camera check fails
        const userAgent =
          navigator.userAgent || navigator.vendor || window.opera;
        const isMobile =
          /android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
        const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(userAgent);
        setIsMobileOrTablet(isMobile || isTablet);
      }
    };

    checkCameraAccess();

    // Cleanup refs on component unmount
    return () => {
      errorToastShownRef.current = false;
      isScanningRef.current = false;
    };
  }, []);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      code: "",
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .trim()
        .required("Please enter the voucher code")
        .matches(
          /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/,
          "Code must be in format: XXXX-XXXX-XXXX",
        ),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      try {
        const response = await post(endpoints.vouchersRedeem, {
          code: values.code,
        });

        const isSuccess = response.data?.success || response.data?.status;

        if (isSuccess) {
          toastr.success(
            response.data?.message ||
              response.message ||
              "Voucher redeemed successfully!",
          );
          validation.resetForm();
        } else {
          setError(
            response.data?.message ||
              response.message ||
              "Voucher redemption failed",
          );
          toastr.error(
            response.data?.message ||
              response.message ||
              "Voucher redemption failed",
          );
        }
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        setError(errorMessage);
        toastr.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (err) {
        console.error("Scanner stop error:", err);
      } finally {
        scannerRef.current = null;
        isScanningRef.current = false;
      }
    }
  };

  const handleScanQR = async () => {
    // Check if running on HTTPS (required for camera access)
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"
    ) {
      toastr.error(
        "Camera access requires HTTPS. Please use a secure connection.",
      );
      return;
    }

    if (!isMobileOrTablet) {
      toastr.warning(
        "QR scanning is only available on mobile and tablet devices",
      );
      return;
    }

    // Reset error state and open scanner
    setCameraError("");
    errorToastShownRef.current = false;
    setShowScanner(true);
  };

  const handleScanResult = (decodedText) => {
    if (decodedText) {
      // Clear any error state on successful scan
      setCameraError("");
      errorToastShownRef.current = false;

      // Extract code from URL if present
      let codeValue = decodedText;
      if (decodedText.includes("code=")) {
        try {
          const url = new URL(decodedText);
          const codeParam = url.searchParams.get("code");
          if (codeParam) {
            codeValue = codeParam;
          }
        } catch (err) {
          // Not a valid URL, use decodedText as-is
        }
      }

      validation.setFieldValue("code", codeValue);
      setShowScanner(false);
      stopScanner();
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;

    // Check if the pasted content is a URL with a code parameter
    if (value.includes("code=")) {
      try {
        const url = new URL(value);
        const codeParam = url.searchParams.get("code");
        if (codeParam) {
          validation.setFieldValue("code", codeParam);
          return;
        }
      } catch (err) {
        // Not a valid URL, proceed with normal handling
      }
    }

    // Normal handling
    validation.handleChange(e);
  };

  // NOTE (fix): this callback is html5-qrcode's per-frame
  // "qrCodeErrorCallback". It fires many times per second for every
  // single frame in which no QR code happens to be decodable — which is
  // completely normal while the camera is actively scanning and simply
  // hasn't found a code yet. It is NOT a camera failure signal.
  //
  // Real camera failures (permission denied, camera busy, no camera,
  // etc.) are already handled separately, via the promise rejection of
  // html5QrCode.start(...).catch(...) at scanner startup. So this
  // per-frame callback should never surface UI error state — doing so
  // was causing the flood of false "unable to access camera" messages
  // even while the camera was working correctly.
  const handleScanError = () => {
    // Intentionally a no-op: per-frame "no code found yet" events are
    // expected and should not be treated as errors.
  };

  const handleCloseScanner = async () => {
    setShowScanner(false);
    setCameraError("");
    errorToastShownRef.current = false;
    await stopScanner();
  };

  // Initialize and start scanner when modal opens
  useEffect(() => {
    if (!showScanner) {
      return undefined;
    }

    // Reset error state when opening scanner
    setCameraError("");
    errorToastShownRef.current = false;

    const html5QrCode = new Html5Qrcode("qr-reader");
    scannerRef.current = html5QrCode;

    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
    };

    html5QrCode
      .start(
        { facingMode: "environment" },
        config,
        handleScanResult,
        handleScanError,
      )
      .then(() => {
        // Scanner started successfully, clear any previous errors
        isScanningRef.current = true;
        setCameraError("");
      })
      .catch((err) => {
        console.error("Scanner start error:", err);
        isScanningRef.current = false;
        // Don't show toastr on scanner start failure - only set error state
        setCameraError(
          "Unable to start camera. Please check camera permissions.",
        );
      });

    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScanner]);

  return (
    <React.Fragment>
      <div className="auth-page">
        <div className="auth-page-grid">
          {/* left brand / marketing panel */}
          <div className="auth-brand-panel">
            <div className="auth-brand-panel-inner">
              <Link to="/" className="auth-logo">
                <img
                  className="auth-logo-icon"
                  src={logo}
                  alt="Omaha Food Lovers"
                />
              </Link>
              <h1 className="auth-headline">
                Restaurant Portal
                <br />
                <span className="auth-headline-accent">Omaha Food Lovers</span>
              </h1>
              <p className="auth-subline">
                Redeem customer vouchers quickly and easily.
                <br />
                Enter the 12-character voucher code or scan the QR code.
              </p>
              <ul className="auth-feature-list">
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={creditCardIcon} alt="Credit Card" />
                  </span>
                  <span>Track voucher redemptions and customer activity.</span>
                </li>
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={giftIcon} alt="Gift" />
                  </span>
                  <span>Manage your restaurant profile and settings.</span>
                </li>
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={zapIcon} alt="Zap" />
                  </span>
                  <span>Quick redemption with QR code scanning.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* right form panel */}
          <div className="auth-form-panel">
            <div className="auth-form-card">
              <div className="auth-form-header">
                <h2>Redeem Voucher</h2>
                <p>Enter the diner's 12-character voucher code</p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="custom-input-row">
                  <CustomInput
                    label="Voucher Code"
                    name="code"
                    type="text"
                    placeholder="XXXX-XXXX-XXXX"
                    value={validation.values.code}
                    onChange={handleCodeChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.code && !!validation.errors.code
                    }
                    errorText={validation.errors.code}
                    className="glass"
                  />
                </div>

                {isMobileOrTablet && (
                  <div className="mb-3">
                    <CustomButton
                      type="button"
                      variant="secondary"
                      onClick={handleScanQR}
                      className="w-100"
                      iconPosition="right"
                      icon={arrowRightIcon}
                      loading={loading}
                    >
                      Scan QR Code
                    </CustomButton>
                  </div>
                )}

                {showScanner && (
                  <div className="qr-scanner-overlay">
                    <div className="qr-scanner-modal">
                      <div className="qr-scanner-header">
                        <h3>Scan QR Code</h3>
                        <button
                          type="button"
                          onClick={handleCloseScanner}
                          className="qr-scanner-close"
                        >
                          ×
                        </button>
                      </div>
                      <div className="qr-scanner-container">
                        <div
                          id="qr-reader"
                          style={{ width: "100%", height: "100%" }}
                        ></div>
                        {cameraError && (
                          <div
                            className="qr-scanner-error"
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              background: "rgba(0,0,0,0.8)",
                              color: "white",
                              padding: "16px",
                              borderRadius: "8px",
                              textAlign: "center",
                              maxWidth: "80%",
                            }}
                          >
                            {cameraError}
                          </div>
                        )}
                      </div>
                      <p className="qr-scanner-hint">
                        Point your camera at the QR code
                      </p>
                    </div>
                  </div>
                )}

                {error ? <p className="auth-server-error">{error}</p> : null}

                <div className="auth-submit-wrap">
                  <CustomButton
                    type="submit"
                    variant="primary"
                    iconPosition="right"
                    icon={arrowRightIcon}
                    loading={loading}
                  >
                    Validate and Redeem
                  </CustomButton>
                </div>
              </form>

              <p className="auth-login-link">
                Back to <Link to="/login?portal=restaurant">Log in</Link>
              </p>
            </div>
          </div>
        </div>
        <p className="auth-brand-footer">
          © Omaha Food Lovers {new Date().getFullYear()} - All Rights Reserved.
        </p>
      </div>
    </React.Fragment>
  );
};

export default withRouter(RestaurantRedeemVoucher);
